/**
 * Audible scan feedback for the counting flow.
 *
 * The target device is an iPhone in a store: Safari implements neither `navigator.vibrate` nor
 * any other signal, so before this a scan that landed was indistinguishable from one that did
 * not. Three WebAudio tones cover the three outcomes without shipping any audio asset —
 * a short high beep for a hit, a double low blip for a repeat inside the dedupe window, and a
 * longer low buzz for a code the catalogue does not know.
 *
 * iOS starts an `AudioContext` suspended unless it was created inside a user gesture, hence
 * `ensureAudioUnlocked()`, which the count layout calls on the first `pointerdown` anywhere in
 * the flow (entering a count is always a tap). Everything here is a no-op where WebAudio is
 * missing — jsdom in the unit tests included.
 */

type AudioContextCtor = new () => AudioContext

let ctx: AudioContext | null = null

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    AudioContext?: AudioContextCtor
    webkitAudioContext?: AudioContextCtor
  }
  return w.AudioContext ?? w.webkitAudioContext ?? null
}

/**
 * Creates (and resumes) the shared AudioContext. Must be reached from inside a user gesture the
 * first time, or iOS keeps it suspended and every later tone is silent.
 */
export function ensureAudioUnlocked() {
  const Ctor = audioContextCtor()
  if (!Ctor) return
  try {
    ctx ??= new Ctor()
    if (ctx.state === 'suspended') void ctx.resume()
  } catch {
    // No audio on this device; the visual signals still fire.
    ctx = null
  }
}

interface Tone {
  freq: number
  ms: number
  /** Offset from the start of the signal, so a two-tone blip needs one call. */
  delayMs?: number
  gain?: number
}

function play(tones: Array<Tone>) {
  ensureAudioUnlocked()
  const audio = ctx
  if (!audio || audio.state !== 'running') return
  for (const tone of tones) {
    const startAt = audio.currentTime + (tone.delayMs ?? 0) / 1000
    const endAt = startAt + tone.ms / 1000
    const osc = audio.createOscillator()
    const gain = audio.createGain()
    osc.type = 'square'
    osc.frequency.value = tone.freq
    // Ramp the tail down: a hard stop on a square wave clicks audibly.
    gain.gain.setValueAtTime(tone.gain ?? 0.08, startAt)
    gain.gain.exponentialRampToValueAtTime(0.0001, endAt)
    osc.connect(gain)
    gain.connect(audio.destination)
    osc.start(startAt)
    osc.stop(endAt)
  }
}

export const scanFeedback = {
  /** A code resolved to an article. */
  hit: () => play([{ freq: 880, ms: 80 }]),
  /** The same code again inside the dedupe window — nothing was captured twice. */
  duplicate: () =>
    play([
      { freq: 330, ms: 60 },
      { freq: 330, ms: 60, delayMs: 110 },
    ]),
  /** The catalogue does not know this code. */
  unknown: () => play([{ freq: 220, ms: 150 }]),
}
